import React, { useMemo, useState } from 'react';
import Animated, {
  runOnJS,
  useAnimatedReaction,
} from 'react-native-reanimated';
import { styled } from 'dripsy';
import { observer } from 'mobx-react-lite';
import { View } from 'react-native';

import { BaseText } from '../../common/BaseText';
import { Touchable, TouchableTypeEnum } from '../../common/Touchable';
import { Icon } from '../../common/Icon';
import { IS_WEB } from '@utils';

const Container = styled(View)({
  flex: 1,
  justifyContent: 'center',
});

const TouchableContainer = styled(Touchable)({
  flexDirection: 'row',
  alignItems: 'center',
});

const Text = styled(BaseText)({
  color: '$background0',
  variant: IS_WEB ? 'text.small' : 'text.xsmall',
  textAlign: 'center',
  fontWeight: 'semi',
  mr: '$4',
});

function findText(
  progress: Animated.SharedValue<number>,
  timestamps: TimestampType,
) {
  'worklet';

  return (
    timestamps.find((v) => v.time <= progress.value) ??
    timestamps[timestamps.length - 1]
  );
}

interface ITimestampsPartType {
  text: string;
  time: number;
}

export type TimestampType = ITimestampsPartType[];

interface ITimestampTextProps {
  progress: Animated.SharedValue<number>;
  timestamps: TimestampType;
  onPress(): void;
}

type CurrentTimestampType = ITimestampsPartType | null;

export const TimestampText = observer<ITimestampTextProps>(
  ({ progress, timestamps, onPress }) => {
    const [part, setPart] = useState<CurrentTimestampType>(null);

    function updateText(newPart?: ITimestampsPartType) {
      if (newPart && newPart.time !== part?.time) {
        setPart(newPart);
      } else if (!newPart) {
        setPart(null);
      }
    }

    const clonedAndReversedTimestamps = useMemo(
      () =>
        timestamps
          .map((i) => ({ ...i }))
          .slice()
          .reverse(),
      [timestamps],
    );

    useAnimatedReaction(
      () => progress,
      (result) => {
        const newPart = findText(result, clonedAndReversedTimestamps);

        runOnJS(updateText)(newPart);
      },
    );

    if (!part) {
      return null;
    }

    const iconSize = IS_WEB ? 20 : 16;

    return (
      <Container>
        <TouchableContainer
          onPress={onPress}
          type={TouchableTypeEnum.Opacity}
        >
          <Text numberOfLines={1}>{part.text}</Text>
          <Icon
            name="small-arrow-right-light"
            color="$background0"
            size={iconSize}
          />
        </TouchableContainer>
      </Container>
    );
  },
);
